import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { CATALOG_KEY, CUSTOM_FIELDS_KEY, useCustomFieldDefs, type CustomFieldDef } from './ItemForm';
import { NumberInput } from '../ui/NumberInput';
import { sameValues, useUnsavedGuard, validForm } from '../ui/unsaved';

const TYPE_LABELS: Record<CustomFieldDef['type'], string> = { text: 'Tekst', number: 'Liczba', boolean: 'Tak / nie', select: 'Lista wyboru' };
const splitOptions = (text: string) => text.split(',').map((o) => o.trim()).filter(Boolean);

function Problem({ error }: { error: unknown }) {
  if (!(error instanceof ApiError)) return null;
  const detail = fieldIssues(error)[0]?.message;
  return (
    <p className="alert" role="alert">
      {error.status === 409 ? 'Ktoś już zmienił to pole. Odśwież stronę i spróbuj ponownie.' : (detail ?? error.message)}
    </p>
  );
}

function DefRow({ def }: { def: CustomFieldDef }) {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState(def.label);
  const [sort, setSort] = useState(String(def.sort));
  const [options, setOptions] = useState((def.options ?? []).join(', '));
  const refresh = () => queryClient.invalidateQueries({ queryKey: CATALOG_KEY });
  const save = useMutation({
    mutationFn: () =>
      api(`/api/catalog/custom-fields/${def.id}`, {
        method: 'PATCH',
        body: { version: def.version, label, sort: Number(sort), ...(def.type === 'select' ? { options: splitOptions(options) } : {}) },
      }),
    onSuccess: refresh,
  });
  const remove = useMutation({ mutationFn: () => api(`/api/catalog/custom-fields/${def.id}?version=${def.version}`, { method: 'DELETE' }), onSuccess: refresh });
  // owner 2026-10-23 21:14: leaving with unsaved changes asks first
  const sortValid = /^-?\d{1,9}$/.test(sort.trim());
  const guardRef = useUnsavedGuard({
    dirty: !sameValues({ label, sort, options }, { label: def.label, sort: String(def.sort), options: (def.options ?? []).join(', ') }),
    save: async (el) => {
      if (!sortValid || !validForm(el)) return false;
      await save.mutateAsync();
      return true;
    },
  });
  return (
    <li className="row">
      <form
        ref={guardRef}
        className="row-main"
        aria-label={`Pole własne: ${def.label}`}
        onSubmit={(e) => {
          e.preventDefault();
          if (sortValid) save.mutate();
        }}
      >
        <div className="muted">
          {TYPE_LABELS[def.type]} · klucz: {def.key}
        </div>
        <Problem error={save.error ?? remove.error} />
        <label className="field">
          <span>Etykieta</span>
          <input value={label} onChange={(e) => setLabel(e.target.value)} required />
        </label>
        {def.type === 'select' && (
          <label className="field">
            <span>Opcje (po przecinku)</span>
            <input value={options} onChange={(e) => setOptions(e.target.value)} />
          </label>
        )}
        <label className="field">
          <span>Kolejność</span>
          <NumberInput kind="integer" value={sort} onValueChange={setSort} />
          {!sortValid && <span className="field-error">Podaj liczbę całkowitą.</span>}
        </label>
        <div className="actions">
          <button className="btn small primary" type="submit" disabled={save.isPending}>
            Zapisz
          </button>
          <button className="btn small" type="button" aria-label={`Usuń pole: ${def.label}`} disabled={remove.isPending} onClick={() => remove.mutate()}>
            Usuń
          </button>
        </div>
      </form>
    </li>
  );
}

/** Definitions of the extra fields every catalog item may have. Only for those with `catalog.settings`. */
export function CustomFieldsSettings() {
  const queryClient = useQueryClient();
  const defs = useCustomFieldDefs().data?.customFields ?? [];
  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');
  const [type, setType] = useState<CustomFieldDef['type']>('text');
  const [options, setOptions] = useState('');
  const add = useMutation({
    mutationFn: () =>
      api('/api/catalog/custom-fields', {
        method: 'POST',
        body: { key: key.trim(), label: label.trim(), type, ...(type === 'select' ? { options: splitOptions(options) } : {}), sort: (defs.at(-1)?.sort ?? 0) + 10 },
      }),
    onSuccess: async () => {
      setKey('');
      setLabel('');
      setOptions('');
      await queryClient.invalidateQueries({ queryKey: CUSTOM_FIELDS_KEY });
    },
  });
  function submit(event: FormEvent) {
    event.preventDefault();
    add.mutate();
  }
  const addRef = useUnsavedGuard({
    dirty: key !== '' || label !== '' || options !== '',
    save: async (el) => {
      if (!validForm(el)) return false;
      await add.mutateAsync();
      return true;
    },
  });
  return (
    <section className="panel" aria-label="Pola własne" data-feature-id="catalog.custom_fields">
      <h2>Pola własne</h2>
      <p className="muted">Dodatkowe pola, które będzie miała każda pozycja katalogu (np. „Impedancja”).</p>
      {defs.length === 0 && <p className="muted">Nie ma jeszcze pól własnych.</p>}
      <ul className="list">
        {defs.map((d) => (
          <DefRow key={`${d.id}-${d.version}`} def={d} />
        ))}
      </ul>
      <form ref={addRef} className="edit" aria-label="Nowe pole własne" onSubmit={submit}>
        <Problem error={add.error} />
        <label className="field">
          <span>Klucz (małe litery, cyfry, _)</span>
          <input value={key} onChange={(e) => setKey(e.target.value)} required pattern="[a-z][a-z0-9_]{0,39}" autoComplete="off" />
        </label>
        <label className="field">
          <span>Etykieta pola</span>
          <input value={label} onChange={(e) => setLabel(e.target.value)} required autoComplete="off" />
        </label>
        <label className="field">
          <span>Typ pola</span>
          <select value={type} onChange={(e) => setType(e.target.value as CustomFieldDef['type'])}>
            {(Object.keys(TYPE_LABELS) as CustomFieldDef['type'][]).map((t) => (
              <option key={t} value={t}>
                {TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </label>
        {type === 'select' && (
          <label className="field">
            <span>Opcje listy (po przecinku)</span>
            <input value={options} onChange={(e) => setOptions(e.target.value)} required />
          </label>
        )}
        <button className="btn" type="submit" disabled={add.isPending}>
          Dodaj pole
        </button>
      </form>
    </section>
  );
}
